import React, { FC, useState } from "react"
import { toast } from "react-toastify"
import { KButton, KInput, KSpan, KTextArea } from "kahuna-base-react-components"

import { assistant } from "../../../apis"
import { errorFilledToastOptions } from "../../../constants"
import { getLanguageJsonFor } from "../../../constants/languages"

const fill = (template: string, values: { [key: string]: string }) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(value), template)

/**
 * The conversation's own strings. Handed in rather than read off the interface language, because the
 * assistant answers in the artist's language and this card sits inside that answer.
 */
type CoManagerWords = ReturnType<typeof getLanguageJsonFor>["co_manager"]

// The refusals the server words a request back with, each with a sentence of its own.
const ERROR_KEYS: { [code: string]: keyof CoManagerWords } = {
  name_required: "label_request_name_required",
  already_requested: "label_request_already_requested",
  too_many_requests: "label_request_too_many_requests"
}

type LabelRequestCardProps = {
  words: CoManagerWords
  // The name the artist gave in the conversation, typed in for them.
  initialName?: string | null
  onSent: (name: string) => void
}

/**
 * The label an artist asks for. A label is opened at idol by Kahuna, never by the artist, so all the
 * card does is file the request: it waits on the admin Label Requests screen, and the label is only
 * created when an admin approves it there.
 */
const LabelRequestCard: FC<LabelRequestCardProps> = ({ words, initialName, onSent }) => {
  const [name, setName] = useState(initialName || "")
  const [note, setNote] = useState("")
  const [sending, setSending] = useState(false)
  const [sentName, setSentName] = useState<string | null>(null)

  const send = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error(words.label_request_name_required, errorFilledToastOptions)
      return
    }

    setSending(true)
    try {
      // Not abortable: the requests share a controller per path, and this one must not be cancelled by
      // whatever the artist types next.
      await assistant("/co-manager/label-request/", "POST", { name: trimmed, note: note.trim() }, {}, false)
      setSentName(trimmed)
      onSent(trimmed)
    } catch (error: any) {
      const key = ERROR_KEYS[error?.response?.data?.error]
      toast.error(key ? words[key] : words.label_request_error, errorFilledToastOptions)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="co-manager-claim-form">
      <KSpan text={words.label_request_title} fontSize={12} lineHeight="16px" fontWeight={500} color="#111" />
      {sentName ? (
        <KSpan text={fill(words.label_request_sent, { name: sentName })} fontSize={12} lineHeight="16px" color="#666" />
      ) : (
        <>
          <KSpan text={words.label_request_hint} fontSize={11} lineHeight="15px" color="#888" />
          <KInput
            dataTestId="co-manager-label-request-name"
            value={name}
            placeholder={words.label_request_name}
            padding="10px"
            onChange={(value) => setName(value)}
            disabled={sending}
          />
          <KTextArea
            value={note}
            placeholder={words.label_request_note}
            padding="10px"
            onChange={(value) => setNote(value)}
            disabled={sending}
            height={56}
          />
          <div className="co-manager-bubble-actions">
            <KButton
              dataTestId="co-manager-send-label-request"
              text={sending ? words.label_request_sending : words.label_request_send}
              onClick={send}
              disabled={sending || !name.trim()}
              background="#111"
              textColor="#FFF"
              width="auto"
              height="34px"
              padding="7px 16px"
              borderRadius={100}
              fontSize={14}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default LabelRequestCard
